import {
  ChangeEvent,
  createContext,
  HTMLAttributes,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { MediaType, MediaTypeSemantic, SubmediaType } from "../types"
import {
  createSubmediaType,
  deleteSubmediaType,
  fetchSubmediaTypeSemantics,
  patchSubmediaType,
} from "api/globalMediaTypes"
import { queryCache, useMutation, useQuery } from "react-query"

import Button from "ui/buttons/Button"
import { ButtonGroup } from "components/buttons/ButtonGroup"
import { MultiSelect } from "../../components/MultiSelect"
import { Paper } from "@material-ui/core"
import SimpleTable from "../../components/SimpleTable"
import { WithAuthority } from "components/WithAuthority"
import assoc from "lodash/fp/set"
import { css } from "@emotion/css"
import get from "lodash/fp/get"
import { useI18n } from "providers/I18n"

type Props = {
  mediaType: Partial<MediaType>
}

export default function SubmediaTypes(props: Props) {
  const { t } = useI18n()
  const { mediaType } = props
  const [state, setState] = useState<SubmediaType[]>(
    mediaType?.submediaTypes || [],
  )
  const [value, setValue] = useState<MediaTypeSemantic[]>([])
  const [modalInfo, setModalInfo] = useState<ModalInfo>()

  // ** fetch submedia types semantics
  const { data: submediaTypesSemantics } = useQuery(
    ["submedia-types-semantics", mediaType?.semanticCode],
    fetchSubmediaTypeSemantics,
    {
      enabled: !!mediaType?.semanticCode,
    },
  )

  // ** add submedia type semantics
  const [add] = useMutation(createSubmediaType, {
    onSuccess: () => queryCache.invalidateQueries("global-media-types"),
    throwOnError: true,
  })

  // ** remove submedia type
  const [remove] = useMutation(deleteSubmediaType, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("global-media-types")
    },
    onError: () => setModalInfo(undefined),
  })

  // ** patch submedia label
  const [patch] = useMutation(patchSubmediaType, {
    onSuccess: () => queryCache.invalidateQueries("global-media-types"),
  })

  const handleDelete = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("admin.submediaType.delete.title"),
        type: "primary_destructive",
        description: t("admin.submediaType.delete.description"),
        submitLabel: t("admin.submediaType.delete.action"),
        onSubmit: async () => await remove(id),
      })
    },
    [remove, t],
  )

  useEffect(() => {
    setState(() => mediaType?.submediaTypes || [])
  }, [mediaType.submediaTypes])

  const isModalOpen = Boolean(modalInfo)
  const subMediaSemanticsMapped =
    submediaTypesSemantics?.map((s) => ({ value: s.code, name: s.name })) || []

  const onUpdate = useCallback(
    async (name, cellName, value, id) => {
      value !== get(cellName, mediaType.submediaTypes) &&
        patch({ id, [name]: value })
    },
    [patch, mediaType.submediaTypes],
  )

  const handleAdd = useCallback(() => {
    const submediaTypes = !!value.length
      ? value.map((v) => ({
          name: v?.name,
          semanticCode: v?.code,
          mediaTypeId: mediaType?.id,
        }))
      : []
    const pld = {
      id: mediaType.id || "",
      submediaTypes,
    }
    add(pld)
    setValue([])
  }, [add, mediaType.id, value])

  const handleChange = (
    evt: ChangeEvent<
      | HTMLInputElement
      | HTMLTextAreaElement
      | {
          name?: string | undefined
          value: unknown
        }
    >,
  ) => {
    const { name, value } = evt.target
    !!name && setState((s) => assoc(name, value, s))
  }

  const handleToggleOption = (selectedOptions: MediaTypeSemantic[]) => {
    const selectedOptionsMapped = selectedOptions?.map((s: any) => ({
      code: s.value,
      name: s.name,
      value: s.value,
    }))
    setValue(selectedOptionsMapped)
  }
  const handleClearOptions = () => setValue([])

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setValue(submediaTypesSemantics || [])
    } else {
      handleClearOptions()
    }
  }

  const handleClose = useCallback(() => {
    setValue([])
  }, [])

  const context = useMemo(
    () => ({
      value,
      handleAdd,
      handleClose,
    }),
    [value, handleAdd, handleClose],
  )

  return (
    <contextProps.Provider value={context}>
      <div className={styles.container}>
        <WithAuthority allowed={["AUTH_EDIT_MEDIA_TYPE"]}>
          <MultiSelect
            label={t("admin.submediaType.add")}
            placeholder={t("admin.submediaType.add.placeholder")}
            items={subMediaSemanticsMapped}
            getOptionLabel={(option: Record<string, string>) => option.name}
            selectedValues={value}
            renderOption={(option: Record<string, string>) => (
              <>
                <span className={styles.option}>{t(option?.value)}</span>{" "}
                <span className={styles.option}>{option?.name}</span>
              </>
            )}
            selectAllOption={{
              name: t("admin.mediaTypes.label"),
              value: "Semantic",
            }}
            selectAllValue="Semantic"
            onToggleOption={handleToggleOption}
            onClearOptions={handleClearOptions}
            onSelectAll={handleSelectAll}
            paperComponent={PaperComponent}
            inputClassName={styles.inputWidth}
            checkValues={() => true}
          />
        </WithAuthority>
        <SimpleTable
          handleDelete={handleDelete}
          data={state}
          onUpdate={onUpdate}
          onChange={handleChange}
        />
        <ConfirmationModal
          open={isModalOpen}
          onCancel={() => setModalInfo(undefined)}
          {...modalInfo}
        />
      </div>
    </contextProps.Provider>
  )
}

const PaperComponent = (props: HTMLAttributes<HTMLElement>) => {
  const { t } = useI18n()
  const { children } = props
  const { value, handleAdd, handleClose } = useContextProps()
  return (
    <Paper>
      {children}
      <ButtonGroup className={styles.buttonGroup}>
        <Button variant="outlined" color="primary" onMouseDown={handleClose}>
          {t("modal.label.cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onMouseDown={handleAdd}
          disabled={!value.length}
        >
          {t("button.add")}
        </Button>
      </ButtonGroup>
    </Paper>
  )
}

const contextProps = createContext<
  | {
      value: MediaTypeSemantic[]
      handleAdd: VoidFunction
      handleClose: VoidFunction
    }
  | undefined
>(undefined)

export const useContextProps = () => {
  const paperProps = useContext(contextProps)

  if (paperProps === undefined) {
    throw new Error("error")
  }
  return paperProps
}

const styles = {
  container: css`
    margin-top: var(--spacing-xxl-3);
  `,
  input: css`
    margin-top: var(--spacing-xxl-3);
  `,
  option: css`
    width: 150px;
    margin: 0 12px 0 4px;
  `,
  buttonGroup: css`
    text-align: right;
    padding: var(--spacing-m);
    background-color: var(--lighter-grey);
  `,
  inputWidth: css`
    width: 250px;
  `,
}
